// Firebase Phone Auth — used for OTP-based password reset.
// One Firebase project is shared by all cafe owners; they never see these
// values. The web API key is safe to expose (it's embedded in the bundle).
import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  isSignInWithEmailLink,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

export function isFirebaseConfigured() {
  return Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.authDomain &&
      firebaseConfig.projectId &&
      firebaseConfig.appId,
  );
}

export function getFirebaseAuth() {
  const app = getApps()[0] || initializeApp(firebaseConfig);
  return getAuth(app);
}

// Lazily create (or reuse) an invisible reCAPTCHA verifier on a container element.
// We keep it on window so multiple OTP attempts from the same modal share it.
// CRITICAL: Firebase throws "reCAPTCHA has already been rendered in this element"
// if a previous verifier's iframe is still mounted in the same container. So
// before creating a new verifier we ALWAYS wipe the container's children.
export function ensureRecaptcha(auth, containerId = "phone-otp-recaptcha") {
  if (typeof window === "undefined") {
    throw new Error("Recaptcha can only be initialized in the browser");
  }
  // Tear down any previously-tracked verifier so its iframe gets torn down
  // gracefully by Google's script. We do NOT touch DOM nodes ourselves —
  // yanking them can crash recaptcha__en.js with "null .style".
  if (window.__recaptchaVerifier) {
    try { window.__recaptchaVerifier.clear(); } catch { /* noop */ }
    window.__recaptchaVerifier = null;
    window.__recaptchaContainerId = null;
  }
  const verifier = new RecaptchaVerifier(auth, containerId, {
    size: "invisible",
  });
  window.__recaptchaVerifier = verifier;
  window.__recaptchaContainerId = containerId;
  return verifier;
}

export function clearRecaptcha() {
  if (typeof window === "undefined") return;
  if (window.__recaptchaVerifier) {
    // RecaptchaVerifier.clear() lets Firebase + Google tear down the iframe
    // they injected. We deliberately DO NOT remove the container DOM node
    // ourselves — Google's recaptcha script may still hold a reference and
    // will crash with "Cannot read properties of null (reading 'style')" if
    // its iframe is yanked from under it.
    try { window.__recaptchaVerifier.clear(); } catch { /* noop */ }
    window.__recaptchaVerifier = null;
    window.__recaptchaContainerId = null;
  }
}

// Detects whether we're running inside an iframe. Firebase's reCAPTCHA cannot
// load inside most iframes (X-Frame-Options + Google's policy), which makes
// signInWithPhoneNumber hang silently forever. We surface this upfront so the
// user can open in a real tab instead of staring at a stuck spinner.
export function isInsideIframe() {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin frame access blocked → definitely in an iframe.
    return true;
  }
}

export async function sendPhoneOtp(e164Phone, { onProgress, containerId } = {}) {
  const auth = getFirebaseAuth();
  // Make sure Firebase uses the user's browser locale for the SMS text.
  auth.useDeviceLanguage();
  const verifier = ensureRecaptcha(auth, containerId || "phone-otp-recaptcha");

  // Probe reCAPTCHA explicitly so a stuck anti-bot check fails fast (15s)
  // instead of hanging the whole signInWithPhoneNumber promise indefinitely.
  onProgress?.("Loading anti-bot check…");
  await Promise.race([
    verifier.verify(),
    new Promise((_, rej) =>
      setTimeout(() => rej(new Error("recaptcha-timeout")), 15000),
    ),
  ]);

  // signInWithPhoneNumber returns a ConfirmationResult — keep it client-side
  // and call .confirm(code) when the user types the OTP.
  onProgress?.("Sending SMS…");
  return signInWithPhoneNumber(auth, e164Phone, verifier);
}


// ---------- Email-link password reset (Firebase Email Link Auth) ----------
// localStorage key used to remember which email the user requested the link
// for, so we can complete `signInWithEmailLink` after they click it in their
// inbox (Firebase requires us to supply the email on the callback).
const EMAIL_LINK_STORAGE_KEY = "cl_emailForSignIn";

export function emailLinkContinueUrl() {
  // Where Firebase should send the user after they click the magic link.
  // Must be on a domain that's listed in Firebase → Authentication →
  // Settings → Authorized domains.
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/reset-password?via=firebase`;
}

export async function sendEmailResetLink(email) {
  const auth = getFirebaseAuth();
  auth.useDeviceLanguage();
  const actionCodeSettings = {
    url: emailLinkContinueUrl(),
    handleCodeInApp: true,
  };
  await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  // Remember the email so we can complete the flow on the callback page
  // without forcing the user to re-type it.
  try {
    window.localStorage.setItem(EMAIL_LINK_STORAGE_KEY, email);
  } catch { /* private-mode / quota — fall back to re-asking */ }
}

export function getStoredEmailForSignIn() {
  try {
    return window.localStorage.getItem(EMAIL_LINK_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

export function clearStoredEmailForSignIn() {
  try {
    window.localStorage.removeItem(EMAIL_LINK_STORAGE_KEY);
  } catch { /* ignore */ }
}

export function isEmailSignInLink(href) {
  const auth = getFirebaseAuth();
  return isSignInWithEmailLink(auth, href);
}

export async function completeEmailSignIn(email, href) {
  const auth = getFirebaseAuth();
  const result = await signInWithEmailLink(auth, email, href);
  // The Firebase user now has a fresh ID token — return it so we can hand
  // it to our backend, which will verify it and update the MongoDB password.
  const idToken = await result.user.getIdToken(/* forceRefresh */ true);
  return { idToken, email: result.user.email || email };
}

// Map raw Firebase error codes to friendly messages.
//
// Firebase doesn't always propagate the underlying Identity Toolkit error
// message ("OPERATION_NOT_ALLOWED", "INVALID_PHONE_NUMBER", etc.) through to
// the thrown error. So we install a one-time fetch interceptor at module load
// to stash the last error body seen from identitytoolkit.googleapis.com —
// `friendlyFirebaseError` reads it so we can surface it on the UI.
let __lastIdentityToolkitError = "";
if (typeof window !== "undefined" && !window.__phoneAuthFetchPatched) {
  const origFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === "string" ? input : input?.url || "";
    const res = await origFetch(input, init);
    try {
      if (
        url.includes("identitytoolkit.googleapis.com") &&
        !res.ok
      ) {
        const cloned = res.clone();
        const body = await cloned.text();
        // eslint-disable-next-line no-console
        console.error("[firebase phone-auth] identitytoolkit error body:", body);
        try {
          const j = JSON.parse(body);
          __lastIdentityToolkitError =
            j?.error?.message || j?.error?.status || body.slice(0, 200);
        } catch {
          __lastIdentityToolkitError = body.slice(0, 200);
        }
      }
    } catch { /* noop */ }
    return res;
  };
  window.__phoneAuthFetchPatched = true;
}

export function friendlyFirebaseError(e) {
  const code = e?.code || "";
  const googleMsg =
    e?.customData?._tokenResponse?.error?.message ||
    e?.customData?.serverResponse?.error?.message ||
    __lastIdentityToolkitError ||
    "";
  // Always log everything so we have a paper trail.
  // eslint-disable-next-line no-console
  console.error(
    "[firebase phone-auth] error:",
    code,
    "—",
    e?.message || e,
    "google:",
    googleMsg,
  );
  // Reset so the next attempt has a clean slate.
  __lastIdentityToolkitError = "";
  // Prefer the actionable underlying Google message when it's available.
  if (googleMsg) {
    const lc = googleMsg.toUpperCase();
    if (lc.includes("OPERATION_NOT_ALLOWED"))
      return "Phone sign-in isn't enabled on this Firebase project. (Console → Authentication → Sign-in method → Phone → Enable)";
    if (lc.includes("MISSING_CLIENT_IDENTIFIER") || lc.includes("INVALID_APP_CREDENTIAL"))
      return "Firebase rejected this site — the App ID or reCAPTCHA token is invalid. Check the apiKey/appId in your config.";
    if (lc.includes("INVALID_PHONE_NUMBER"))
      return "That phone number looks invalid. Use full international format (e.g. +91 followed by 10 digits).";
    if (lc.includes("BILLING_NOT_ENABLED"))
      return "This Firebase project needs the Blaze (pay-as-you-go) billing plan for phone OTPs. Free tier of 10K/month is still free, but billing must be linked.";
    if (lc.includes("QUOTA_EXCEEDED") || lc.includes("TOO_MANY_ATTEMPTS"))
      return "Too many OTP attempts. Please wait a few minutes and try again.";
    if (lc.includes("UNAUTHORIZED_DOMAIN"))
      return "This domain isn't in Firebase Authorized domains. Add it in Console → Authentication → Settings → Authorized domains.";
    return `Google rejected the OTP: ${googleMsg}`;
  }
  switch (code) {
    case "auth/invalid-phone-number":
      return "That phone number looks invalid — please include the country code.";
    case "auth/missing-phone-number":
      return "Please enter your phone number.";
    case "auth/too-many-requests":
      return "Too many attempts — please wait a few minutes and try again.";
    case "auth/quota-exceeded":
      return "We've hit today's free SMS quota — please try again tomorrow.";
    case "auth/invalid-verification-code":
      return "That code is incorrect. Please double-check the SMS and try again.";
    case "auth/code-expired":
      return "This OTP has expired. Please request a new one.";
    case "auth/unauthorized-domain":
      return "This site isn't authorized to send OTPs yet. Ask the platform admin to add it to Firebase.";
    case "auth/captcha-check-failed":
      return "Anti-bot check failed. Refresh the page and try again.";
    case "auth/network-request-failed":
      return "Couldn't reach Firebase. Most likely the app's domain isn't in Firebase → Authentication → Settings → Authorized domains, or Email/Password sign-in (with the 'Email link' toggle) isn't enabled.";
    default:
      return e?.message || "Something went wrong — please try again.";
  }
}
